/** Metadata attached to browser notifications for session deep links. */
export interface SessionNotificationMetadata {
  notification_id?: unknown;
  session_id?: unknown;
  run_id?: unknown;
  status?: unknown;
}

export interface NotificationNavigationTarget {
  focus: () => void;
  location: Pick<Location, "assign">;
}

/** Accept only application project routes, including task deep links. */
export function isSafeNotificationActionUrl(value: unknown): value is string {
  if (typeof value !== "string" || !value.startsWith("/project/") ||
    value.includes("//") || value.includes("\\") || /[\u0000-\u001F\u007F]/.test(value)) return false;
  try {
    return !value.split(/[?#]/, 1)[0].split("/").some((part) =>
      [".", ".."].includes(decodeURIComponent(part)));
  } catch {
    return false;
  }
}

function getStringMetadata(
  data: SessionNotificationMetadata | null | undefined,
  key: keyof SessionNotificationMetadata,
): string | null {
  const value = data?.[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Turn notification metadata into the UI route. Session IDs remain metadata
 * rather than notification links so navigation stays in the current app UI.
 */
export function buildNotificationSessionPath(
  data: SessionNotificationMetadata | null | undefined,
): string {
  const sessionId = getStringMetadata(data, "session_id");
  const path = sessionId ? `/session/${encodeURIComponent(sessionId)}` : "/";
  const params = new URLSearchParams();

  const runId = getStringMetadata(data, "run_id");
  const status = getStringMetadata(data, "status");
  const notificationId = getStringMetadata(data, "notification_id");
  if (runId) params.set("runId", runId);
  if (status) params.set("notifStatus", status);
  // The service worker carries this through the click URL. Once the React
  // app has authenticated, it marks the durable recipient row as read.
  if (notificationId) params.set("notificationId", notificationId);

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/** Focus the current app window and route its UI to the notification session. */
export function focusNotificationSession(
  data: SessionNotificationMetadata | null | undefined,
  target: NotificationNavigationTarget = window,
): void {
  try {
    target.focus();
  } catch {
    // A focus failure should not prevent same-window session navigation.
  }

  target.location.assign(buildNotificationSessionPath(data));
}
